/**
 *
 * Cleaners are run before uploading to the server. They may modify the contents
 * of an attribute in a way that annoys the user (eg, removing spaces) so they
 * should not be run on the interface unless/until the user has navigated away
 * (eg, an on:change or on:blur of a field).
 *
 * Clean should also be used before checking if two entities are the same.
 *
 */
import type {
	Account,
	AccountToPost,
	CalculatedInvoice,
	Client,
	ClientToPost,
	InvoiceToPost,
	Payment
} from '$lib/types/client_types'

import { getAccTax } from './stores/taxes/manager'

/**
 *
 * Apply this to a calculated invoice to prep for saving and/or comparison
 *
 */
export function cleanInvoice(invoice: CalculatedInvoice): CalculatedInvoice {
	// Using 'calculated' here to remind that the invoice should be normalized and calculated
	// first.
	return {
		...invoice,
		heading: invoice.heading.trim(),
		number: invoice.number.trim(),
		reference: invoice.reference.trim(),
		color: invoice.color,
		notes: invoice.notes.trim(),
		terms: invoice.terms.trim()
	}
}

export function getInvoiceToPost(invoice: CalculatedInvoice): InvoiceToPost {

	const postInvoice = {
		...invoice, 
		taxes: invoice.taxes.map((tax) => ({
			...tax,
			name: tax.name ? tax.name : getAccTax(tax.tax_id)?.name
		}))
	}

	return postInvoice
}

export function getAccountToPost(account: Account): AccountToPost {
	return {
		name: account.name,
		email_visible: account.email_visible,
		phone: account.phone,
		address_1: account.address_1,
		address_2: account.address_2,
		city: account.city,
		state_region: account.state_region,
		postal_code: account.postal_code,
		country: account.country,
		disable_branding: account.disable_branding,
		logo: account.logo,
		color: account.color,
		taxes: account.taxes,
		invoice_default_heading: account.invoice_default_heading,
		invoice_default_notes: account.invoice_default_notes,
		invoice_default_terms: account.invoice_default_terms,
		invoice_copy_with_client: account.invoice_copy_with_client,
		qs_source: account.qs_source,
		reminder_invoice_overdue: account.reminder_invoice_overdue,
		reminder_invoice_overdue_days: account.reminder_invoice_overdue_days,
		reminder_invoice_overdue_days_1: account.reminder_invoice_overdue_days_1,
		reminder_invoice_overdue_days_2: account.reminder_invoice_overdue_days_2,
		estimate_default_heading: account.estimate_default_heading,
		estimate_default_notes: account.estimate_default_notes,
		estimate_default_terms: account.estimate_default_terms,
		estimate_approve_process: account.estimate_approve_process,
		estimate_copy_with_client: account.estimate_copy_with_client,
		country_id: account.country_id,
		currency_id: account.currency_id || 'usd',
		notifications_enabled: account.notifications_enabled,
		default_results_per_page: account.default_results_per_page,
		send_automatic_receipt: account.send_automatic_receipt,
		stub_show_pay_link: account.stub_show_pay_link,
		listing_type: account.listing_type,
		site_url: account.site_url,
		template_type: account.template_type,
		language_id: account.language_id,
		information: account.information,

		openai_thread_id: account.openai_thread_id,
		openai_data_consent: account.openai_data_consent,
		openai_tokens: account.openai_tokens
	}
}

export function getClientToPost(client: Client): ClientToPost {
	return {
		id: client.id,
		name: client.name,
		email: client.email,
		address_1: client.address_1,
		address_2: client.address_2,
		city: client.city,
		country: client.country,
		country_id: client.country_id?.trim() == '' ? 'US' : client.country_id,
		phone: client.phone,
		postal_code: client.postal_code,
		state_region: client.state_region,
		language_id: client.language_id,
		information: client.information
	}
}

export function cleanPayment(payment: Payment): Payment {
	return {
		...payment,
		notes: payment.notes.trim()
	}
}

export function cleanPayments(payments: Payment[]): Payment[] {
	return payments.map(cleanPayment)
}

/**
 *
 * The only fields that are saved in the db, cleaned up
 *
 */
export function cleanClient(client: Client): Client {
	return {
		...client,
		name: client.name.trim(),
		email: client.email.trim(),
		phone: client.phone.trim(),
		city: client.city ? client.city.trim() : '',
		country: client.country ? client.country.trim() : '',
		address_1: client.address_1.trim(),
		address_2: client.address_2.trim(),
		postal_code: client.postal_code ? client.postal_code.trim() : '',
		state_region: client.state_region ? client.state_region.trim() : '',
		country_id: client.country_id ? client.country_id.trim() : '',
		information: client.information ? client.information.trim() : ''
	}
}


export function cleanAccount(account: Account): Account {
	// console.log('cleanAccount - account.country_id: ', account.country_id)
	return {
		...account,
		name: account.name.trim(),
		phone: account.phone.trim(),
		city: account.city.trim(),
		address_1: account.address_1.trim(),
		address_2: account.address_2.trim(),
		postal_code: account.postal_code.trim(),
		state_region: account.state_region.trim(),
		invoice_default_heading: account.invoice_default_heading.trim(),
		invoice_default_notes: account.invoice_default_notes.trim(),
		invoice_default_terms: account.invoice_default_terms.trim(),
		estimate_default_heading: account.estimate_default_heading.trim(),
		estimate_default_notes: account.estimate_default_notes.trim(),
		estimate_default_terms: account.estimate_default_terms.trim(),
		country_id: account.country_id.trim(),
		currency_id: account.currency_id.trim(),
		information: account.information.trim()
	}
}
